/**
 * 研究データをPDFとしてエクスポートするユーティリティ
 */

export interface ResearchPDFData {
  overview: any;
  logs: any[];
  designs: any[];
  literatureNotes: any[];
  figures: any[];
  termDefinitions: any[];
  outputs: any[];
}

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

/** テキストから連続する句点を正規化 */
const cleanText = (text: string | null | undefined): string => {
  if (!text) return '';
  return text.replace(/。。+/g, '。').trim();
};

/** progress_metrics から構造化サマリーを取得（配列・オブジェクト両対応） */
const extractStructuredFromMetrics = (metrics: any): Record<string, string> | null => {
  if (!metrics) return null;
  if (Array.isArray(metrics)) {
    const found = metrics.find((m: any) => m.type === 'structured_summary');
    if (found?.data && typeof found.data === 'object') return found.data;
  } else if (typeof metrics === 'object') {
    if (metrics.structured_summary?.data) return metrics.structured_summary.data;
  }
  return null;
};

/** summary JSON文字列をパース */
const parseStructuredSummary = (raw: string | null | undefined): Record<string, string> => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, string>;
    }
    return {};
  } catch {
    return {};
  }
};

const buildHTML = (theme: string, data: ResearchPDFData): string => {
  const { overview, logs, designs, literatureNotes, figures, termDefinitions, outputs } = data;

  // 研究手順データの取得（オブジェクト形式のみ）
  const researchProcedure = (overview?.progress_metrics && !Array.isArray(overview.progress_metrics))
    ? overview.progress_metrics?.research_procedure || null
    : null;

  const sectionStyle = `margin-bottom: 32px; page-break-inside: avoid;`;

  const headingStyle = `
    font-size: 18px;
    font-weight: bold;
    color: #0f766e;
    border-bottom: 2px solid #0f766e;
    padding-bottom: 6px;
    margin-bottom: 16px;
    margin-top: 20px;
  `;

  const subHeadingStyle = `
    font-size: 14px;
    font-weight: bold;
    color: #0f766e;
    margin-top: 16px;
    margin-bottom: 10px;
  `;

  const fieldBlockStyle = `
    margin-bottom: 14px;
    padding: 12px 14px;
    background: #f8fafc;
    border-left: 3px solid #14b8a6;
    border-radius: 3px;
  `;

  const labelStyle = `
    font-size: 11px;
    color: #64748b;
    font-weight: bold;
    margin-bottom: 4px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  `;

  const valueStyle = `
    font-size: 13px;
    color: #1e293b;
    line-height: 1.75;
    white-space: pre-wrap;
    word-break: break-word;
  `;

  const listItemStyle = `
    margin-bottom: 14px;
    padding: 12px 14px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
  `;

  const renderFieldBlock = (label: string, value: string | null | undefined) => {
    if (!value || !value.trim()) return '';
    // ★ cleanText関数を適用して連続する句点を正規化
    const cleanValue = cleanText(value);
    return `
      <div style="${fieldBlockStyle}">
        <div style="${labelStyle}">${label}</div>
        <div style="${valueStyle}">${cleanValue}</div>
      </div>
    `;
  };

  // 構造化データを取得（progress_metrics優先）
  const metricsStructured = extractStructuredFromMetrics(overview?.progress_metrics);
  const summaryStructured = parseStructuredSummary(overview?.summary);
  const structured = metricsStructured || summaryStructured;
  const isStructured = Object.keys(structured).length > 0;

  // 表紙ページ
  const coverPage = `
    <div style="page-break-after: always; min-height: 900px; display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center;">
      <div style="font-size: 13px; color: #64748b; margin-bottom: 32px; letter-spacing: 0.2em;">研究経過報告書</div>
      <div style="font-size: 28px; font-weight: bold; color: #0f766e; margin-bottom: 48px; line-height: 1.6; max-width: 600px;">${cleanText(theme)}</div>
      <div style="font-size: 13px; color: #94a3b8; margin-top: 80px;">
        <div>出力日：${formatDate(new Date().toISOString())}</div>
      </div>
    </div>
  `;

  // 目次ページ
  let tocNum = 1;
  const tocItems: string[] = [];
  if (overview) tocItems.push(`<div style="font-size: 14px; margin-bottom: 6px;">${tocNum++}. 研究概要</div>`);
  if (researchProcedure) tocItems.push(`<div style="font-size: 14px; margin-bottom: 6px;">${tocNum++}. 研究手順</div>`);
  if (logs.length > 0) tocItems.push(`<div style="font-size: 14px; margin-bottom: 6px;">${tocNum++}. 進捗ログ（${logs.length}件）</div>`);
  if (designs.length > 0) tocItems.push(`<div style="font-size: 14px; margin-bottom: 6px;">${tocNum++}. 研究デザイン・分析方法（${designs.length}件）</div>`);
  if (literatureNotes.length > 0) tocItems.push(`<div style="font-size: 14px; margin-bottom: 6px;">${tocNum++}. 参考文献（${literatureNotes.length}件）</div>`);
  if (termDefinitions.length > 0) tocItems.push(`<div style="font-size: 14px; margin-bottom: 6px;">${tocNum++}. 用語集（${termDefinitions.length}件）</div>`);
  if (figures.length > 0) tocItems.push(`<div style="font-size: 14px; margin-bottom: 6px;">${tocNum++}. 図表（${figures.length}件）</div>`);
  if (outputs.length > 0) tocItems.push(`<div style="font-size: 14px; margin-bottom: 6px;">${tocNum++}. 研究成果物（${outputs.length}件）</div>`);

  const tocPage = `
    <div style="page-break-after: always; min-height: 400px;">
      <div style="${headingStyle}">目次</div>
      <div style="padding-left: 16px; line-height: 2.0;">
        ${tocItems.join('')}
      </div>
    </div>
  `;

  // 研究概要セクション
  let secNum = 1;
  const overviewSection = overview ? (() => {
    const titleBlock = `
      <div style="margin-bottom: 24px;">
        <div style="${labelStyle}">研究タイトル</div>
        <div style="font-size: 17px; font-weight: bold; color: #0f766e; line-height: 1.6; margin-top: 6px;">${cleanText(overview.title) || '（タイトルなし）'}</div>
      </div>
    `;

    const summaryBlocks = isStructured ? `
      ${(structured.background_social || structured.background_academic || structured.background_personal) ? `
        <div style="${subHeadingStyle}">背景</div>
        ${renderFieldBlock('社会的背景', structured.background_social)}
        ${renderFieldBlock('学術的背景', structured.background_academic)}
        ${renderFieldBlock('個人的動機', structured.background_personal)}
      ` : ''}
      ${(structured.purpose_what || structured.purpose_how || structured.purpose_scope) ? `
        <div style="${subHeadingStyle}">目的</div>
        ${renderFieldBlock('何を明らかにするか', structured.purpose_what)}
        ${renderFieldBlock('どうやって明らかにするか', structured.purpose_how)}
        ${renderFieldBlock('対象範囲', structured.purpose_scope)}
        ${renderFieldBlock('介入群', structured.intervention_group)}
        ${renderFieldBlock('対照群', structured.control_group)}
      ` : ''}
      ${(structured.significance_academic || structured.significance_social || structured.significance_practical) ? `
        <div style="${subHeadingStyle}">意義</div>
        ${renderFieldBlock('学術的意義', structured.significance_academic)}
        ${renderFieldBlock('社会的意義', structured.significance_social)}
        ${renderFieldBlock('実務的意義', structured.significance_practical)}
      ` : ''}
    ` : renderFieldBlock('研究概要', overview.summary);

    const rqBlock = overview.rq ? renderFieldBlock('リサーチクエスチョン（RQ）', overview.rq) : '';

    const hypothesesBlock = Array.isArray(overview.hypotheses) && overview.hypotheses.length > 0 ? `
      <div style="${fieldBlockStyle}">
        <div style="${labelStyle}">研究仮説</div>
        ${overview.hypotheses.map((h: string, i: number) => `
          <div style="${valueStyle} margin-bottom: 10px;">
            <strong>仮説${i + 1}：</strong>${cleanText(h)}
          </div>
        `).join('')}
      </div>
    ` : '';

    const goalBlock = overview.next_month_goal ? renderFieldBlock('次の1ヶ月の最重要目標', overview.next_month_goal) : '';

    const dateBlock = `
      <div style="margin-top: 24px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #64748b;">
        登録日：${formatDate(overview.created_at)}
      </div>
    `;

    return `
      <div style="${sectionStyle}">
        <div style="${headingStyle}">${secNum}. 研究概要</div>
        ${titleBlock}
        ${summaryBlocks}
        ${rqBlock}
        ${hypothesesBlock}
        ${goalBlock}
        ${dateBlock}
      </div>
    `;
  })() : '';
  if (overview) secNum++;

  // 研究手順セクション
  const procedureSection = researchProcedure ? `
    <div style="${sectionStyle}">
      <div style="${headingStyle}">${secNum}. 研究手順</div>
      <div style="margin-bottom: 20px;">
        <div style="${labelStyle}">手順タイトル</div>
        <div style="font-size: 15px; font-weight: bold; color: #0f766e; margin-top: 6px;">${cleanText(researchProcedure.title)}</div>
        <div style="font-size: 11px; color: #94a3b8; margin-top: 4px;">最終更新：${formatDate(researchProcedure.updated_at)}</div>
      </div>
      ${Array.isArray(researchProcedure.steps) ? researchProcedure.steps.map((step: any, index: number) => `
        <div style="${listItemStyle}">
          <div style="display: flex; align-items: flex-start; gap: 10px;">
            <div style="flex-shrink: 0; width: 28px; height: 28px; background: #e0f2fe; color: #0369a1; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 13px; text-align: center; line-height: 28px;">
              ${step.order || index + 1}
            </div>
            <div style="flex: 1;">
              <div style="font-size: 14px; font-weight: bold; color: #0f766e; margin-bottom: 8px;">${cleanText(step.title)}</div>
              <div style="${valueStyle}">${cleanText(step.description)}</div>
            </div>
          </div>
        </div>
      `).join('') : ''}
    </div>
  ` : '';
  if (researchProcedure) secNum++;

  // 進捗ログセクション
  const logsSection = logs.length > 0 ? `
    <div style="${sectionStyle}">
      <div style="${headingStyle}">${secNum}. 進捗ログ</div>
      ${logs.map((log, index) => `
        <div style="${listItemStyle}">
          <div style="font-size: 13px; font-weight: bold; color: #0f766e; margin-bottom: 12px;">ログ ${index + 1}</div>
          ${renderFieldBlock('期間', log.period)}
          ${renderFieldBlock('目標', log.objective)}
          ${renderFieldBlock('実施内容', log.actions)}
          ${renderFieldBlock('成果・気づき', log.results)}
          ${renderFieldBlock('次のステップ', log.next_steps)}
          <div style="margin-top: 12px; font-size: 11px; color: #94a3b8;">記録日：${formatDate(log.created_at)}</div>
        </div>
      `).join('')}
    </div>
  ` : '';
  if (logs.length > 0) secNum++;

  // 研究デザイン・分析方法セクション
  const designsSection = designs.length > 0 ? `
    <div style="${sectionStyle}">
      <div style="${headingStyle}">${secNum}. 研究デザイン・分析方法</div>
      ${designs.map((d, index) => `
        <div style="${listItemStyle}">
          <div style="font-size: 13px; font-weight: bold; color: #0f766e; margin-bottom: 10px;">${cleanText(d.title) || `分析方法 ${index + 1}`}</div>
          ${renderFieldBlock('分析手法', d.methodology)}
          ${renderFieldBlock('詳細説明', d.description)}
          <div style="margin-top: 12px; font-size: 11px; color: #94a3b8;">記録日：${formatDate(d.created_at)}</div>
        </div>
      `).join('')}
    </div>
  ` : '';
  if (designs.length > 0) secNum++;

  // 参考文献セクション
  const litSection = literatureNotes.length > 0 ? `
    <div style="${sectionStyle}">
      <div style="${headingStyle}">${secNum}. 参考文献</div>
      ${literatureNotes.map((n, index) => {
        const author = cleanText(n.author) || '著者不明';
        const year = n.year ? `（${n.year}）` : '';
        const title = cleanText(n.title) || '（タイトルなし）';
        return `
          <div style="${listItemStyle}">
            <div style="font-size: 11px; color: #64748b; margin-bottom: 6px;">[${index + 1}]</div>
            <div style="font-size: 14px; font-weight: bold; color: #1e293b; margin-bottom: 6px;">${author} ${year}</div>
            <div style="font-size: 13px; color: #475569; margin-bottom: 10px; font-style: italic;">${title}</div>
            ${n.url ? renderFieldBlock('URL', n.url) : ''}
            ${renderFieldBlock('メモ・要約', n.notes)}
            <div style="margin-top: 12px; font-size: 11px; color: #94a3b8;">記録日：${formatDate(n.created_at)}</div>
          </div>
        `;
      }).join('')}
    </div>
  ` : '';
  if (literatureNotes.length > 0) secNum++;

  // 用語集セクション
  const termSection = termDefinitions.length > 0 ? `
    <div style="${sectionStyle}">
      <div style="${headingStyle}">${secNum}. 用語集</div>
      ${termDefinitions.map(t => `
        <div style="${listItemStyle}">
          <div style="font-size: 15px; font-weight: bold; color: #0f766e; margin-bottom: 10px;">${cleanText(t.term) || '（用語なし）'}</div>
          ${renderFieldBlock('定義', t.definition)}
          ${renderFieldBlock('補足説明', t.notes)}
          <div style="margin-top: 12px; font-size: 11px; color: #94a3b8;">記録日：${formatDate(t.created_at)}</div>
        </div>
      `).join('')}
    </div>
  ` : '';
  if (termDefinitions.length > 0) secNum++;

  // 図表セクション
  const figuresSection = figures.length > 0 ? `
    <div style="${sectionStyle}">
      <div style="${headingStyle}">${secNum}. 図表</div>
      ${figures.map((f, index) => {
        const imageHtml = f.image_url ? `
          <div style="margin: 16px 0; text-align: center;">
            <img src="${f.image_url}"
                 alt="${cleanText(f.title) || '図表'}"
                 style="max-width: 100%; height: auto; border-radius: 6px; border: 1px solid #e2e8f0;"
                 onerror="this.style.display='none';" />
          </div>
        ` : '';
        return `
          <div style="${listItemStyle}">
            <div style="font-size: 13px; font-weight: bold; color: #0f766e; margin-bottom: 10px;">図${index + 1}：${cleanText(f.title) || '（タイトルなし）'}</div>
            ${imageHtml}
            ${renderFieldBlock('説明', f.description)}
            <div style="margin-top: 12px; font-size: 11px; color: #94a3b8;">記録日：${formatDate(f.created_at)}</div>
          </div>
        `;
      }).join('')}
    </div>
  ` : '';
  if (figures.length > 0) secNum++;

  // 研究成果物セクション
  const outputsSection = outputs.length > 0 ? `
    <div style="${sectionStyle}">
      <div style="${headingStyle}">${secNum}. 研究成果物</div>
      ${outputs.map((o, index) => `
        <div style="${listItemStyle}">
          <div style="font-size: 13px; font-weight: bold; color: #0f766e; margin-bottom: 10px;">${cleanText(o.title) || `成果物 ${index + 1}`}</div>
          ${renderFieldBlock('種別', o.output_type)}
          ${renderFieldBlock('説明', o.description)}
          ${o.file_url ? renderFieldBlock('ファイルURL', o.file_url) : ''}
          <div style="margin-top: 12px; font-size: 11px; color: #94a3b8;">記録日：${formatDate(o.created_at)}</div>
        </div>
      `).join('')}
    </div>
  ` : '';

  return `
    <!DOCTYPE html>
    <html lang="ja">
    <head>
      <meta charset="UTF-8" />
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          font-family: 'Hiragino Kaku Gothic ProN', 'Hiragino Sans', 'Meiryo', 'Yu Gothic', 'MS Gothic', sans-serif;
          font-size: 13px;
          color: #1e293b;
          background: #ffffff;
          padding: 36px 44px;
          width: 794px;
          line-height: 1.75;
        }
      </style>
    </head>
    <body>
      ${coverPage}
      ${tocPage}
      ${overviewSection}
      ${procedureSection}
      ${logsSection}
      ${designsSection}
      ${litSection}
      ${termSection}
      ${figuresSection}
      ${outputsSection}
      <div style="margin-top: 48px; padding-top: 24px; border-top: 2px solid #e2e8f0; text-align: center;">
        <div style="font-size: 11px; color: #94a3b8; margin-bottom: 6px;">研究経過報告書</div>
        <div style="font-size: 13px; font-weight: bold; color: #0f766e;">${cleanText(theme)}</div>
      </div>
    </body>
    </html>
  `;
};

export const exportResearchToPDF = async (
  theme: string,
  data: ResearchPDFData,
): Promise<void> => {
  const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
    import('jspdf'),
    import('html2canvas'),
  ]);

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.top = '-9999px';
  iframe.style.left = '-9999px';
  iframe.style.width = '794px';
  iframe.style.height = '1123px';
  iframe.style.border = 'none';
  iframe.style.visibility = 'hidden';
  document.body.appendChild(iframe);

  try {
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) throw new Error('iframe document not available');

    iframeDoc.open();
    iframeDoc.write(buildHTML(theme, data));
    iframeDoc.close();

    const hasImages = data.figures.some(f => f.image_url);
    await new Promise(resolve => setTimeout(resolve, hasImages ? 2000 : 600));

    const body = iframeDoc.body;
    const totalHeight = body.scrollHeight;

    const canvas = await html2canvas(body, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 794,
      height: totalHeight,
      windowWidth: 794,
      windowHeight: totalHeight,
      logging: false,
    });

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    // A4: 210 x 297mm、余白 15mm
    const margin = 15;
    const pageWidth = 210;
    const pageHeight = 297;
    const contentWidth = pageWidth - margin * 2;
    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * contentWidth) / canvas.width;
    const contentHeight = pageHeight - margin * 2;

    let yOffset = 0;
    let remainingHeight = imgHeight;
    let pageNumber = 1;

    while (remainingHeight > 0) {
      if (yOffset > 0) {
        pdf.addPage();
        pageNumber++;
      }

      const sliceHeight = Math.min(contentHeight, remainingHeight);
      const srcY = (yOffset / imgHeight) * canvas.height;
      const srcH = (sliceHeight / imgHeight) * canvas.height;

      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = srcH;
      const ctx = pageCanvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
      }
      const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
      pdf.addImage(pageImgData, 'JPEG', margin, margin, imgWidth, sliceHeight);

      pdf.setFontSize(8);
      pdf.setTextColor(148, 163, 184);
      pdf.text(`${pageNumber}`, pageWidth / 2, pageHeight - 8, { align: 'center' });

      yOffset += contentHeight;
      remainingHeight -= contentHeight;
    }

    const safeTheme = theme.replace(/[\\/:*?"<>|]/g, '_').slice(0, 50);
    pdf.save(`研究経過報告_${safeTheme}.pdf`);
  } catch (error) {
    console.error('PDFエクスポートエラー:', error);
    throw error;
  } finally {
    document.body.removeChild(iframe);
  }
};
